import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  Box,
  IconButton,
  CircularProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useSelector, useDispatch } from "react-redux";
import {
  techProfileUsernameCheckAction,
  techProfileUsernameCheckResetAction,
} from "../../../actions/MyTechProfile/myTechProfileActions";

const PublishConfirmationModal = ({
  open,
  onClose,
  onGoLive,
  previewUrlBase,
  existingUsername = null, // New prop to pass existing username
}) => {
  const dispatch = useDispatch();
  const { loading, success, error, usernameDetails } = useSelector(
    (state) => state.techProfileUsernameCheckStore
  );
  const [username, setUsername] = useState("");
  const [edited, setEdited] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Determine if this is first time publishing or editing existing
  const isFirstTimePublish = !existingUsername;

  // Reset state when the modal is closed
  useEffect(() => {
    if (!open) {
      setUsername(existingUsername || "");
      setEdited(false);
      setIsEditMode(false);
      dispatch(techProfileUsernameCheckResetAction());
    } else {
      // Set initial values when modal opens
      setUsername(existingUsername || "");
      setIsEditMode(isFirstTimePublish);
    }
  }, [open, dispatch, existingUsername, isFirstTimePublish]);

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    if (!edited) {
      setEdited(true);
    }
    // If user edits after a success, reset the success state
    if (success) {
      dispatch(techProfileUsernameCheckResetAction());
    }
  };

  const handleCheckUsername = () => {
    if (username.trim()) {
      dispatch(techProfileUsernameCheckAction(username.trim()));
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setEdited(false);
    dispatch(techProfileUsernameCheckResetAction());
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setUsername(existingUsername || "");
    setEdited(false);
    dispatch(techProfileUsernameCheckResetAction());
  };

  const handleGoLiveClick = () => {
    if (isFirstTimePublish) {
      // First time publish - need username check success
      if (username.trim() && success) {
        onGoLive(username.trim());
      }
    } else {
      // Existing username scenario
      if (isEditMode && username.trim() && success) {
        // User edited and checked new username
        onGoLive(username.trim());
      } else if (!isEditMode && existingUsername) {
        // User keeping existing username
        onGoLive(existingUsername);
      }
    }
  };

  const previewLink = username.trim()
    ? `${previewUrlBase}/${username.trim()}`
    : "";

  // Determine if Go Live button should be enabled
  const isGoLiveEnabled = () => {
    if (isFirstTimePublish) {
      return success && !loading;
    } else {
      if (isEditMode) {
        return success && !loading;
      } else {
        return true; // Can go live with existing username
      }
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return; // ✅ prevent close on outside click
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="publish-dialog-title"
      PaperProps={{
        sx: {
          background: "rgba(18, 18, 18, 0.85)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          width: "90%",
          maxWidth: "420px",
          color: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
          padding: "32px",
          fontFamily: "Questrial, sans-serif",
        },
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 1,
      }}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          top: "10px",
          right: "10px",
          color: "#bbbbbb",
          "&:hover": {
            color: "#ffffff",
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle
        id="publish-dialog-title"
        sx={{
          fontWeight: 600,
          fontFamily: "'Questrial', sans-serif",
          padding: 0,
          marginBottom: "8px",
        }}
      >
        {isFirstTimePublish
          ? "Go Live with Your Profile"
          : "Update Your Profile"}
      </DialogTitle>

      <DialogContent sx={{ padding: 0, width: "100%" }}>
        <DialogContentText
          sx={{
            color: "#b0b0b0",
            fontFamily: "'Questrial', sans-serif",
            fontSize: "1rem",
            marginBottom: "24px",
          }}
        >
          {isFirstTimePublish
            ? "Create a unique username for your portfolio. This will be part of your public URL."
            : "Your portfolio is already live. You can update your username if needed."}
        </DialogContentText>

        {/* Existing Username Display (Non-edit Mode) */}
        {!isFirstTimePublish && !isEditMode && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "rgba(138, 43, 226, 0.1)",
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "16px",
              border: "1px solid rgba(138, 43, 226, 0.3)",
            }}
          >
            <Box sx={{ textAlign: "left" }}>
              <Typography
                sx={{
                  color: "#b0b0b0",
                  fontSize: "0.75rem",
                  marginBottom: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Current Username
              </Typography>
              <Typography
                sx={{
                  color: "#ffffff",
                  fontSize: "1rem",
                  fontWeight: 500,
                  fontFamily: "'Questrial', sans-serif",
                }}
              >
                {existingUsername}
              </Typography>
            </Box>
            <IconButton
              onClick={handleEditClick}
              sx={{
                color: "#8a2be2",
                backgroundColor: "rgba(138, 43, 226, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(138, 43, 226, 0.2)",
                  color: "#9945d9",
                },
                padding: "8px",
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Username Input Field (Edit Mode or First Time) */}
        {(isFirstTimePublish || isEditMode) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 2,
              width: "100%",
            }}
          >
            <TextField
              autoFocus
              margin="dense"
              id="username"
              label="Username"
              type="text"
              fullWidth
              variant="standard"
              value={username}
              onChange={handleUsernameChange}
              placeholder="e.g., jane-doe"
              InputLabelProps={{
                sx: {
                  color: "#b0b0b0",
                  "&.Mui-focused": { color: "#8a2be2" },
                },
              }}
              sx={{
                input: { color: "#ffffff" },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#b0b0b0",
                },
                "& .MuiInput-underline:after": { borderBottomColor: "#8a2be2" },
              }}
            />

            {edited && !success && !loading && (
              <Button
                variant="outlined"
                onClick={handleCheckUsername}
                disabled={!username.trim()}
                sx={{
                  color: "#8a2be2",
                  borderColor: "#8a2be2",
                  whiteSpace: "nowrap",
                  height: 36,
                  mb: "4px",
                  fontFamily: "'Questrial', sans-serif",
                  textTransform: "inherit",
                  "&:hover": {
                    backgroundColor: "rgba(138, 43, 226, 0.1)",
                    borderColor: "#7925c9",
                  },
                }}
              >
                Check
              </Button>
            )}

            {loading && (
              <Box sx={{ mb: "10px" }}>
                <CircularProgress size={24} sx={{ color: "#8a2be2" }} />
              </Box>
            )}
          </Box>
        )}

        {/* Cancel Edit Button */}
        {!isFirstTimePublish && isEditMode && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button
              onClick={handleCancelEdit}
              size="small"
              sx={{
                color: "#b0b0b0",
                textTransform: "inherit",
                fontFamily: "'Questrial', sans-serif",
                "&:hover": {
                  color: "#ffffff",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        )}

        {/* Error Message */}
        {error && (
          <Typography
            sx={{
              color: "#ff4d4f",
              fontSize: "0.85rem",
              marginTop: "8px",
              textAlign: "left",
            }}
          >
            {error}
          </Typography>
        )}

        {/* Success Message */}
        {success && (
          <Typography
            sx={{
              color: "#52c41a",
              fontSize: "0.85rem",
              marginTop: "8px",
              textAlign: "left",
            }}
          >
            Username is available!
          </Typography>
        )}

        {/* Preview Link */}
        {previewLink && (
          <Box
            sx={{
              mt: 2,
              bgcolor: "rgba(138, 43, 226, 0.1)",
              p: 1.5,
              borderRadius: 2,
              textAlign: "left",
            }}
          >
            <DialogContentText sx={{ color: "#e0e0e0", fontSize: "0.9rem" }}>
              Your portfolio will be available at:
              <br />
              <span style={{ color: "#c792ea", wordBreak: "break-all" }}>
                {previewLink}
              </span>
            </DialogContentText>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "12px",
          marginTop: "24px",
          padding: 0,
        }}
      >
        <Button
          onClick={handleGoLiveClick}
          variant="contained"
          disabled={!isGoLiveEnabled() || loading}
          fullWidth
          sx={{
            backgroundColor: "#8a2be2",
            color: "#fff",
            textTransform: "inherit",
            fontFamily: "'Questrial', sans-serif",
            fontWeight: 500,
            padding: "8px 16px",
            "&:hover": {
              backgroundColor: "#7925c9",
            },
            "&:disabled": {
              backgroundColor: "#4a4a4a",
              color: "#888",
            },
          }}
        >
          {isFirstTimePublish
            ? "Go Live"
            : isEditMode
            ? "Update & Go Live"
            : "Go Live"}
        </Button>
        <Button
          onClick={onClose}
          fullWidth
          sx={{
            backgroundColor: "transparent",
            border: "1px solid #8a2be2",
            color: "#ffffff",
            textTransform: "inherit",
            fontFamily: "'Questrial', sans-serif",
            fontWeight: 500,
            padding: "8px 16px",
            "&:hover": {
              backgroundColor: "rgba(138, 43, 226, 0.1)",
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishConfirmationModal;
